import React, { useEffect, useState } from "react";
import {
  Grid,
  Popover,
  makeStyles,
  OutlinedInput,
  List,
  ListItem,
  ListItemText,
  ButtonBase,
  ListItemAvatar,
  Avatar,
  Typography
} from "@material-ui/core";
import { connect } from "react-redux";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { useSnackbar } from "notistack";

import { FormattedText } from "../common/FormattedText";
import OptimizedTooltip from "../common/OptimizedTooltip";
import API from "../../utils/axios";
import { client } from "../map/DriverMarkers";
import { setChangedVehicles } from "../../store/actions/project";
import { setSolution } from "../../store/actions/solution";
import { setLoadingRoutesList } from "../../store/actions/loading";
import { openAddDriverDialog } from "../../store/actions/common";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";

const useStyles = makeStyles(theme => ({
  menuPaper: {
    width: 320,
    boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.16)"
  },
  menuContentWrapper: {
    overflow: "hidden"
  },
  searchWrapper: {
    padding: theme.spacing(1.25, 1.1),
    borderBottom: "1px solid #EAEBF0"
  },
  searchInput: {
    padding: theme.spacing(1.5)
  },
  noDriverNotice: {
    fontSize: theme.typography.pxToRem(14),
    color: "#202124",
    textAlign: "center",
    padding: theme.spacing(1.125, 0)
  },
  listWrapper: {
    padding: theme.spacing(1, 0),
    maxHeight: 216,
    overflowY: "auto"
  },
  list: {
    padding: 0
  },
  listItem: {
    padding: theme.spacing(0.93, 2.25),
    "& .more-info-icon": {
      opacity: 0,
      visibility: "hidden",
      color: "#9D9EA2"
    },
    "&:hover .more-info-icon": {
      opacity: 1,
      visibility: "visible"
    }
  },
  listItemAvatar: {
    minWidth: 42
  },
  driverAvatar: {
    width: 26,
    height: 26,
    backgroundColor: "#1329FE",
    fontSize: theme.typography.pxToRem(12),
    fontWeight: theme.typography.fontWeightMedium
  },
  noDriverAvatarIcon: {
    fontSize: theme.typography.pxToRem(31),
    color: "#C2C3C7"
  },
  listItemText: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightRegular,
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    color: "#202124",
    maxWidth: 200,
    paddingRight: 10
  },
  driverDetailsTitle: {
    fontFamily: "Roboto",
    fontStyle: "normal",
    fontWeight: 500,
    fontSize: "0.825rem",
    lineHeight: "1rem",
    color: "#FFFFFF"
  },
  driverDetailsContent: {
    paddingTop: "0.25rem",
    fontFamily: "Roboto",
    fontStyle: "normal",
    fontWeight: 400,
    fontSize: "0.825rem",
    lineHeight: "1rem",
    color: "#FFFFFF"
  },
  driverDetailsTooltip: {
    width: 248,
    maxWidth: 248,
    padding: theme.spacing(1.25, 1.5)
  },
  newDriverWrapper: {
    height: 56,
    maxHeight: 56,
    borderTop: "1px solid #EAEBF0",
    padding: theme.spacing(1, 0)
  },
  newDriverButton: {
    width: "100%",
    color: theme.palette.primary.main,
    padding: theme.spacing(1.25, 1.85),
    textAlign: "left",
    fontSize: theme.typography.pxToRem(15),
    justifyContent: "flex-start",
    "&:hover": {
      backgroundColor: "#F4F4F4"
    }
  },
  newDriverIcon: {
    marginRight: theme.spacing(2)
  }
}));

const AssignDriverMenu = props => {
  const {
    mainRouteId,
    anchorElement,
    onClose,
    drivers,
    solution,
    projectId,
    setChangedVehicles,
    setSolution,
    setLoadingRoutesList,
    openAddDriverDialog
  } = props;

  // General
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  // States
  const [searchInputValue, setSearchInputValue] = useState("");
  const [filteredItems, setFilteredItems] = useState(drivers);

  // Callbacks
  const onNewDriverClick = () => {
    onClose();
    openAddDriverDialog();
  };

  const assignDriver = driverId => {
    onClose();
    setLoadingRoutesList(true);
    let relatedRouteIds = [];
    const routeGroup =
      solution.routeGroups[Object.keys(solution.routeGroups)[0]];
    routeGroup.forEach(routes => {
      const routesId = routes.map(route => route.id);
      if (routes.map(route => route.id).includes(mainRouteId)) {
        relatedRouteIds = routesId;
      }
    });

    const newSolution = { ...solution };
    Promise.all(
      relatedRouteIds.map(theRouteId => {
        const routeIndex = solution.routes.findIndex(
          route => route.id === theRouteId
        );
        // Count the change
        const route = newSolution.routes[routeIndex];
        setChangedVehicles(route.vehicle_id);
        return API.put(
          `/v1/projects/${projectId}/routes/${theRouteId}/drivers`,
          {
            driver_id: driverId
          }
        );
      })
    )
      .then(results => {
        results.forEach(res => {
          const routeIndex = solution.routes.findIndex(
            route => route.id === res.data.id
          );
          client &&
            client.send(
              JSON.stringify({
                type: "driver_change",
                route_id: res.data.id,
                driver_id: driverId
              })
            );
          newSolution.routes[routeIndex] = res.data;
        });
      })
      .catch(error => {
        enqueueSnackbar("Something went wrong");
        console.log(error);
      })
      .finally(() => {
        setSolution(newSolution);
        setLoadingRoutesList(false);
      });
  };

  const unassignDriver = () => {
    assignDriver(null);
  };

  // Effects
  useEffect(() => {
    setFilteredItems(
      drivers.filter(driver => {
        return (
          (driver.first_name + " " + driver.last_name)
            .toLowerCase()
            .indexOf(searchInputValue) !== -1 ||
          driver.email.toLowerCase().indexOf(searchInputValue) !== -1
        );
      })
    );
  }, [drivers, searchInputValue]);

  return (
    <>
      <Popover
        open={anchorElement !== null}
        onClose={() => {
          onClose();
          setTimeout(() => {
            setSearchInputValue("");
            setFilteredItems(drivers);
          }, 200);
        }}
        anchorEl={anchorElement}
        classes={{
          paper: classes.menuPaper
        }}
        PaperProps={{
          elevation: 0
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
      >
        <Grid
          container
          direction="column"
          className={classes.menuContentWrapper}
        >
          <Grid item className={classes.searchWrapper}>
            <OutlinedInput
              placeholder={"Assign to..."}
              fullWidth
              classes={{ input: classes.searchInput }}
              onChange={event => {
                const value = event.target.value.toLowerCase();
                setSearchInputValue(value);
              }}
            />
          </Grid>
          <Grid
            item
            alignItems={"center"}
            alignContent={"center"}
            justify={"center"}
            className={classes.listWrapper}
          >
            {filteredItems.length === 0 && (
              <Typography className={classes.noDriverNotice}>
                No drivers to display
              </Typography>
            )}
            {filteredItems.length > 0 && (
              <List className={classes.list}>
                <ListItem
                  key={"unassigned"}
                  button
                  className={classes.listItem}
                  onClick={unassignDriver}
                >
                  <ListItemAvatar className={classes.listItemAvatar}>
                    <Avatar
                      className={classes.driverAvatar}
                      style={{ backgroundColor: "transparent" }}
                    >
                      <AccountCircleIcon
                        className={classes.noDriverAvatarIcon}
                      />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    disableTypography
                    className={classes.listItemText}
                  >
                    Unassigned
                  </ListItemText>
                </ListItem>
                {filteredItems.map(item => (
                  <ListItem
                    key={item.id}
                    button
                    className={classes.listItem}
                    onClick={() => {
                      assignDriver(item.id);
                      setSearchInputValue("");
                    }}
                  >
                    <ListItemAvatar className={classes.listItemAvatar}>
                      <Avatar className={classes.driverAvatar}>{`${
                        item.first_name ? item.first_name[0] : ""
                      }${item.last_name ? item.last_name[0] : ""}`}</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      disableTypography
                      className={classes.listItemText}
                    >
                      <FormattedText
                        text={item.first_name + " " + item.last_name}
                        query={searchInputValue}
                      />
                    </ListItemText>
                    <OptimizedTooltip
                      placement="bottom"
                      classes={{ tooltip: classes.driverDetailsTooltip }}
                      title={
                        <>
                          {item.email && (
                            <>
                              <Typography
                                component="h6"
                                className={classes.driverDetailsTitle}
                              >
                                Email
                              </Typography>
                              <Typography
                                component="p"
                                className={classes.driverDetailsContent}
                              >
                                {item.email}
                              </Typography>
                            </>
                          )}
                          {item.mobile_number && (
                            <>
                              <br />
                              <Typography
                                component="h6"
                                className={classes.driverDetailsTitle}
                              >
                                Phone number
                              </Typography>
                              <Typography
                                component="p"
                                className={classes.driverDetailsContent}
                              >
                                {item.mobile_number}
                              </Typography>
                            </>
                          )}
                        </>
                      }
                    >
                      <InfoOutlinedIcon className={"more-info-icon"} />
                    </OptimizedTooltip>
                  </ListItem>
                ))}
              </List>
            )}
          </Grid>
          <Grid item className={classes.newDriverWrapper}>
            <ButtonBase
              className={classes.newDriverButton}
              onClick={onNewDriverClick}
            >
              <AddCircleOutlineIcon
                fontSize="small"
                color="primary"
                className={classes.newDriverIcon}
              />
              New driver
            </ButtonBase>
          </Grid>
        </Grid>
      </Popover>
    </>
  );
};

function mapStateToProps(state) {
  return {
    drivers: state.project.drivers || [],
    solution: state.project.solution,
    projectId: state.project.id
  };
}

export default connect(mapStateToProps, {
  setSolution,
  setLoadingRoutesList,
  setChangedVehicles,
  openAddDriverDialog
})(AssignDriverMenu);
